import React from "react";
import axios from "axios";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Copy, Clock, Image as ImageIcon, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "../../utils/clipboard";
import RegenerateSection from "./RegenerateSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function FacebookTab({ content, onRegenerate, isRegenerating, onRegenerateItem, regeneratingItem }) {
  const [postImages, setPostImages] = React.useState({});
  const [generatingImage, setGeneratingImage] = React.useState(null);

  const generateImage = async (post, index) => {
    setGeneratingImage(index);
    try {
      const prompt = `Create an engaging Facebook post image: ${post.type} - ${post.content.substring(0, 200)}`;
      
      const response = await axios.post(`${API}/images/generate`, {
        prompt,
        content_type: "facebook"
      });

      setPostImages(prev => ({
        ...prev,
        [index]: response.data.image_url
      }));

      toast.success("Image generated!");
    } catch (error) {
      console.error("Image generation error:", error);
      toast.error("Failed to generate image");
    } finally {
      setGeneratingImage(null);
    }
  };

  if (!content || content.length === 0) {
    return (
      <div className="text-center text-white/60 py-12">
        <p className="mb-4">No Facebook content generated yet.</p>
        {onRegenerate && <RegenerateSection onRegenerate={onRegenerate} isRegenerating={isRegenerating} contentType="Facebook" />}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-white">Facebook Posts</h2>
        <p className="text-white/60">{content.length} posts optimized for engagement</p>
      </div>

      {/* Posts */}
      <div className="space-y-6">
        {content.map((post, index) => (
          <Card key={index} className="card">
            <CardContent className="pt-6 space-y-4">
              {/* Post Header */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1877f2] to-[#0d5dbf] flex items-center justify-center text-white font-bold">
                  EM
                </div>
                <div>
                  <div className="font-semibold text-white">Epsillon Media</div>
                  <div className="text-xs text-white/40">Just now · 🌍</div>
                </div>
              </div>

              {/* Post Type Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full">
                <span className="text-lg">{post.emoji}</span>
                <span className="text-sm font-semibold text-white">{post.type}</span>
              </div>

              {/* Generated Image */}
              {postImages[index] && (
                <div className="rounded-lg overflow-hidden">
                  <img 
                    src={postImages[index]} 
                    alt={`Facebook Post ${index + 1}`}
                    className="w-full h-64 object-cover"
                  />
                </div>
              )}

              {/* Post Content */}
              <p className="text-white text-lg whitespace-pre-wrap leading-relaxed">
                {post.content}
              </p>

              {/* Image Direction */}
              <div className="p-4 bg-white/5 rounded-lg border-l-4 border-[#00d4aa]">
                <div className="text-xs font-mono text-white/40 uppercase mb-1">
                  Image Direction
                </div>
                <div className="text-sm text-white/80">{post.imageDirection}</div>
              </div>

              {/* Generate Image Button */}
              <Button
                onClick={() => generateImage(post, index)}
                disabled={generatingImage === index}
                variant="outline"
                className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
              >
                {generatingImage === index ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Image...
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Generate Image with Gemini
                  </>
                )}
              </Button>

              {/* Best Time */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div className="flex items-center gap-2 text-sm text-white/60">
                  <Clock className="w-4 h-4" />
                  <span>Best time to post: {post.bestTime}</span>
                </div>
                <div className="flex items-center gap-2">
                  {onRegenerateItem && (
                    <button
                      onClick={() => onRegenerateItem(index)}
                      disabled={regeneratingItem === index}
                      className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                      title="Regenerate this post"
                    >
                      <RefreshCw size={13} className={regeneratingItem === index ? 'animate-spin' : ''} />
                    </button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(post.content)}
                    className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Regenerate Section */}
      {onRegenerate && (
        <RegenerateSection
          onRegenerate={onRegenerate}
          isRegenerating={isRegenerating}
          contentType="Facebook"
        />
      )}
    </div>
  );
}

export default FacebookTab;